import {
  ACCENT, BOX, COOL, bloom, elementsFor, mark, organic, plane, spread, stroke, svgRoot, trianglePath,
} from './svg.js';

// Nine movements, in a Bauhaus vocabulary: circle, square, triangle, bold flat
// planes, heavy/thin stroke contrast, asymmetric balance. Two palette colours
// overlap at partial opacity so the translucency does the mixing, and a blurred
// bloom sits behind so the geometry lands on a field of colour rather than on
// bare paper.
//
// Each motif still answers "what is the prose doing here?", never "what is the
// prose about" — that distinction is the whole difference between a visual that
// belongs to an essay and clipart dropped on top of one.

const MID = BOX.height / 2;

/** An argument drawing to a point. Bars angle into a single disc. */
function converge(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width - 120, cy: MID, r: 96, opacity: 0.4 });
  const group = organic(svg, { strength: 2.4, seed: 4 });
  const bars = elementsFor(intensity, 3, 6);
  const targetX = BOX.width - 132;

  for (let index = 0; index < bars; index += 1) {
    const y = 34 + ((BOX.height - 68) * index) / Math.max(1, bars - 1);
    const height = 9 + (index % 2) * 5;
    group.appendChild(plane('path', {
      d: `M40 ${(y - height / 2).toFixed(1)} L${targetX} ${(MID - 5).toFixed(1)} `
        + `L${targetX} ${(MID + 5).toFixed(1)} L40 ${(y + height / 2).toFixed(1)} Z`,
      opacity: (0.22 + (index % 3) * 0.14).toFixed(2),
    }, { order: index, from: 'left' }));
  }
  group.appendChild(plane('circle', {
    cx: targetX, cy: MID, r: 46, fill: ACCENT, opacity: 0.55,
  }, { order: bars, from: 'scale' }));
  group.appendChild(mark('circle', { cx: targetX, cy: MID, r: 13 }, { order: bars + 1 }));
  return svg;
}

/** A tension splitting something open. A square broken along a hard diagonal. */
function fracture(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width / 2, cy: MID, r: 104, colour: ACCENT, opacity: 0.34 });
  const group = organic(svg, { strength: 3.4, seed: 9 });
  const gap = elementsFor(intensity, 10, 30);
  const left = BOX.width / 2 - 104;
  const right = BOX.width / 2 + 104;

  group.appendChild(plane('path', {
    d: `M${left} 42 L${right - 40} 42 L${left + 52} ${BOX.height - 42} L${left} ${BOX.height - 42} Z`,
    transform: `translate(${-gap / 2} 0)`,
    opacity: 0.62,
  }, { order: 0, from: 'left' }));
  group.appendChild(plane('path', {
    d: `M${right - 30} 42 L${right} 42 L${right} ${BOX.height - 42} L${left + 62} ${BOX.height - 42} Z`,
    transform: `translate(${gap / 2} 0)`,
    fill: ACCENT,
    opacity: 0.58,
  }, { order: 1, from: 'right' }));

  const wedges = elementsFor(intensity, 1, 3);
  for (let index = 0; index < wedges; index += 1) {
    group.appendChild(mark('path', {
      d: trianglePath(BOX.width / 2, 76 + index * 54, 13, 180),
      opacity: (0.9 - index * 0.22).toFixed(2),
    }, { order: index + 2 }));
  }
  return svg;
}

/** Evidence building. Stacked bars, alternating weight, capped by a disc. */
function accumulate(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width - 130, cy: 96, r: 78, colour: ACCENT, opacity: 0.34 });
  const group = organic(svg, { strength: 1.8, seed: 6 });
  const columns = elementsFor(intensity, 5, 10);
  const baseline = BOX.height - 44;
  const width = Math.min(30, (BOX.width - 100) / columns - 8);

  spread(columns, 52).forEach((x, index) => {
    const height = 24 + (index / Math.max(1, columns - 1)) * (BOX.height - 116);
    group.appendChild(plane('rect', {
      x: (x - width / 2).toFixed(1),
      y: (baseline - height).toFixed(1),
      width: width.toFixed(1),
      height: height.toFixed(1),
      fill: index % 2 ? ACCENT : 'currentColor',
      opacity: index % 2 ? 0.5 : 0.72,
    }, { order: index, from: 'bottom' }));
  });

  group.appendChild(stroke('path', {
    d: `M44 ${baseline} L${BOX.width - 44} ${baseline}`, 'stroke-width': 3,
  }, { length: BOX.width, order: 0 }));
  group.appendChild(mark('circle', {
    cx: spread(columns, 52).at(-1), cy: baseline - (BOX.height - 92) - 6, r: 15, fill: ACCENT,
  }, { order: columns }));
  return svg;
}

/** A claim thinning out. A solid plane dissolving into a shrinking grid. */
function erode(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: 110, cy: MID, r: 92, opacity: 0.4 });
  const group = organic(svg, { strength: 4.2, seed: 12 });
  const columns = elementsFor(intensity, 7, 13);
  const rows = 3;

  group.appendChild(plane('rect', {
    x: 40, y: MID - 62, width: 74, height: 124, opacity: 0.74,
  }, { order: 0, from: 'left' }));

  spread(columns, 130).forEach((x, index) => {
    const share = index / Math.max(1, columns - 1);
    const size = 26 * (1 - share * 0.72);
    const surviving = Math.max(1, Math.round((1 - share) * rows) + 1);
    for (let row = 0; row < surviving; row += 1) {
      const y = MID + (row - (surviving - 1) / 2) * 46;
      group.appendChild(plane('rect', {
        x: (x - size / 2).toFixed(1),
        y: (y - size / 2).toFixed(1),
        width: size.toFixed(1),
        height: size.toFixed(1),
        fill: row % 2 ? ACCENT : 'currentColor',
        opacity: (0.7 - share * 0.5).toFixed(2),
      }, { order: index, from: 'scale' }));
    }
  });
  return svg;
}

/** Crossing a line. A disc split by a bar, half in each colour. */
function threshold(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width / 2, cy: MID, r: 100, colour: COOL, opacity: 0.3 });
  const group = organic(svg, { strength: 2, seed: 21 });
  const radius = 62;
  const cx = BOX.width / 2;

  group.appendChild(plane('path', {
    d: `M${cx - radius} ${MID} A${radius} ${radius} 0 0 1 ${cx + radius} ${MID} Z`,
    opacity: 0.66,
  }, { order: 0, from: 'up' }));
  group.appendChild(plane('path', {
    d: `M${cx + radius} ${MID} A${radius} ${radius} 0 0 1 ${cx - radius} ${MID} Z`,
    fill: ACCENT, opacity: 0.55,
  }, { order: 1, from: 'bottom' }));

  group.appendChild(plane('rect', {
    x: 34, y: MID - 4, width: BOX.width - 68, height: 8, opacity: 0.9,
  }, { order: 2, from: 'left' }));

  const crossings = elementsFor(intensity, 1, 3);
  spread(crossings + 2, 74).slice(1, crossings + 1).forEach((x, index) => {
    group.appendChild(mark('path', {
      d: trianglePath(x, MID - 34, 14, 0), fill: index ? ACCENT : 'currentColor',
    }, { order: index + 3 }));
  });
  return svg;
}

/** Rhythm, back and forth. Alternating half-discs on a baseline. */
function oscillate(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width / 2, cy: MID, r: 110, colour: ACCENT, opacity: 0.28 });
  const group = organic(svg, { strength: 2.2, seed: 15 });
  const lobes = elementsFor(intensity, 4, 7);
  const radius = Math.min(42, (BOX.width - 80) / lobes / 2);

  spread(lobes, 44 + radius).forEach((x, index) => {
    const up = index % 2 === 0;
    const sweep = up ? 1 : 0;
    group.appendChild(plane('path', {
      d: `M${(x - radius).toFixed(1)} ${MID} A${radius.toFixed(1)} ${radius.toFixed(1)} 0 0 ${sweep} ${(x + radius).toFixed(1)} ${MID} Z`,
      fill: up ? 'currentColor' : ACCENT,
      opacity: up ? 0.66 : 0.52,
    }, { order: index, from: up ? 'up' : 'bottom' }));
  });

  group.appendChild(stroke('path', {
    d: `M36 ${MID} L${BOX.width - 36} ${MID}`, 'stroke-width': 2.5,
  }, { length: BOX.width, order: 0 }));
  return svg;
}

/** Circling something unresolved. Concentric rings, one square set askew. */
function orbit(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width / 2, cy: MID, r: 96, opacity: 0.36 });
  const group = organic(svg, { strength: 1.6, seed: 30 });
  const cx = BOX.width / 2;
  const rings = elementsFor(intensity, 2, 4);

  group.appendChild(plane('rect', {
    x: cx - 54, y: MID - 54, width: 108, height: 108,
    transform: `rotate(45 ${cx} ${MID})`,
    fill: ACCENT, opacity: 0.3,
  }, { order: 0, from: 'scale' }));

  for (let index = 0; index < rings; index += 1) {
    const r = 34 + index * 26;
    group.appendChild(stroke('circle', {
      cx, cy: MID, r, 'stroke-width': index === 0 ? 5 : 1.5,
    }, { length: Math.round(2 * Math.PI * r), order: index }));
  }
  group.appendChild(mark('circle', {
    cx: cx + 34 + (rings - 1) * 26, cy: MID, r: 12, fill: ACCENT,
  }, { order: rings }));
  return svg;
}

/** A system or structure. The Bauhaus grid: some cells filled, some open. */
function lattice(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width / 2, cy: MID, r: 104, colour: COOL, opacity: 0.3 });
  const group = organic(svg, { strength: 1.4, seed: 44 });
  const columns = elementsFor(intensity, 4, 7);
  const rows = 3;
  const cell = Math.min(46, (BOX.width - 96) / columns);
  const originX = (BOX.width - cell * columns) / 2;
  const originY = MID - (cell * rows) / 2;

  for (let column = 0; column < columns; column += 1) {
    for (let row = 0; row < rows; row += 1) {
      // A regular alternation, broken often enough to stay a composition.
      const filled = (column + row * 2) % 3 === 0;
      const accent = (column * row) % 4 === 1;
      group.appendChild(plane('rect', {
        x: (originX + column * cell).toFixed(1),
        y: (originY + row * cell).toFixed(1),
        width: cell.toFixed(1),
        height: cell.toFixed(1),
        fill: filled ? (accent ? ACCENT : 'currentColor') : 'none',
        stroke: 'currentColor',
        'stroke-width': filled ? 0 : 1.4,
        opacity: filled ? (accent ? 0.5 : 0.66) : 0.4,
      }, { order: column + row, from: 'scale' }));
    }
  }
  return svg;
}

/** Narrowing or opening. An iris of triangular segments around an open centre. */
function aperture(intensity) {
  const svg = svgRoot();
  bloom(svg, { cx: BOX.width / 2, cy: MID, r: 92, colour: ACCENT, opacity: 0.34 });
  const group = organic(svg, { strength: 2.6, seed: 51 });
  const cx = BOX.width / 2;
  const blades = elementsFor(intensity, 4, 8);

  for (let index = 0; index < blades; index += 1) {
    const angle = (360 / blades) * index;
    group.appendChild(plane('path', {
      d: `M${cx} ${MID - 96} L${cx + 40} ${MID - 40} L${cx - 40} ${MID - 40} Z`,
      transform: `rotate(${angle.toFixed(1)} ${cx} ${MID})`,
      fill: index % 2 ? ACCENT : 'currentColor',
      opacity: index % 2 ? 0.46 : 0.6,
    }, { order: index, from: 'scale' }));
  }
  group.appendChild(stroke('circle', {
    cx, cy: MID, r: 30, 'stroke-width': 5,
  }, { length: Math.round(2 * Math.PI * 30), order: blades }));
  return svg;
}

export const MOTIF_RENDERERS = {
  converge,
  fracture,
  accumulate,
  erode,
  threshold,
  oscillate,
  orbit,
  lattice,
  aperture,
};
